/**
 * Session expiry while there is unsaved work (§12 autosave): instead of dropping the cache
 * and redirecting to `/login` (which would unmount the annotator and lose its pending
 * edits), a 401 opens the re-login dialog (`layout/ReloginModal`). After signing in again
 * the unsaved work is flushed; "Sign out" leaves (edits stay in the browser's draft store).
 */
import type { QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { setUnauthorizedHandler } from '@/api/client';
import { resetSession } from '@/api/hooks/auth';
import { qk } from '@/api/keys';
import type { Me } from '@/api/types';
import { hasUnsavedWork } from '@/lib/unsavedWork';

export interface ReauthState {
  open: boolean;
  /** Page to return to when the user gives up and signs in on the login page. */
  nextPath: string | null;
  /** The user whose session expired (prefills the dialog; a different user is refused). */
  user: Pick<Me, 'id' | 'email'> | null;
}

export const useReauth = create<ReauthState>()(() => ({ open: false, nextPath: null, user: null }));

type Waiter = (signedIn: boolean) => void;
const waiters = new Set<Waiter>();

/** Opens the re-login dialog (idempotent while it is open). */
export function requireReauth(nextPath: string, user: Pick<Me, 'id' | 'email'> | null): void {
  const state = useReauth.getState();
  if (state.open) return;
  useReauth.setState({ open: true, nextPath, user: user ?? state.user });
}

/** Closes the dialog; `signedIn` tells waiters whether to retry their requests. */
export function resolveReauth(signedIn: boolean): void {
  useReauth.setState({ open: false });
  for (const waiter of [...waiters]) waiter(signedIn);
  waiters.clear();
}

/** Resolves once the dialog is closed (true = signed in again); immediately if it is closed. */
export function waitForReauth(): Promise<boolean> {
  if (!useReauth.getState().open) return Promise.resolve(true);
  return new Promise((resolve) => waiters.add(resolve));
}

/**
 * A 401 anywhere: with unsaved work → re-login dialog, keep the page; otherwise forget the
 * cached data so `RequireAuth` redirects to `/login?next=<page>`.
 */
export function handleUnauthorized(queryClient: QueryClient, nextPath: string): void {
  if (hasUnsavedWork() || useReauth.getState().open) {
    const me = queryClient.getQueryData<Me | null>(qk.auth.me()) ?? null;
    requireReauth(nextPath, me ? { id: me.id, email: me.email } : null);
    return;
  }
  resetSession(queryClient, null);
}

/** Wires {@link handleUnauthorized} into the API client (main.tsx, tests). */
export function installUnauthorizedHandler(queryClient: QueryClient): void {
  setUnauthorizedHandler((nextPath) => handleUnauthorized(queryClient, nextPath));
}
